import PageNames from "./page_names";
import { IJsonModel } from "flexlayout-react";
import getProjectSummaryPanelsLayout from "../../../services/panels/get_project_summary_panels_layout";
import getPanelsLayout from "../../../services/panels/get_panels_layout";
import getPanelsLayoutForMode from "../../../services/panels/get_panels_layout_for_mode";
import { InspectionMode } from "./inspection_modes";
import { doPanelFlagsMatch, PanelFlags } from "./panel_flag";
import { Predicate } from "avvir/source/typings/underscore";
import { isBoolean, isFunction } from "underscore";

import { PanelStateModel } from "../../../services/panels/panel_state_model";

export const PANELS_PAGE_REVISIONS_LOCAL_STORAGE_KEY = "panelsPageRevisions";
export const PANELS_MODEL_BY_PAGE = "panelsModelByPage";
// The Explorer keeps its own key so the pre-reskin layout is never overwritten, which is what
// makes turning the flag back off safe. The mode rides along: a layout saved in another mode is
// not this mode's.
export const PANELS_MODEL_BY_PAGE_AND_MODE = "panelsModelByPageAndMode";
export const PANEL_TYPES_SEEN = "panelTypesSeen";

export enum PanelType {
  ACTION_HISTORY = "action_history",
  COMMENTS = "comments",
  ELEMENT_GROUPS = "element_groups",
  ELEMENT_INSPECTOR = "element_inspector",
  INSPECT_PANEL = "inspect_panel",
  INSPECT_REPORTS = "inspect_reports",
  MINIMAP = "minimap",
  PHOTO_VIEWER = "photo_viewer",
  PROJECT_AREA_SUMMARY = "project_area_summary",
  PROJECT_AREAS_MODEL_VIEWER = "project_areas_model_viewer",
  PROJECT_FILES = "project_files",
  VIEWER = "viewer",
  CUSTOM_REPORTS_PANEL = "custom_reports_panel", //JDC: this probably needs to be deleted.
  PROGRESS_4D_REPORT_PANEL = "progress_4d_report_panel"
}

export type PanelFlagPredicate = boolean | PanelFlags | Predicate<PanelFlags>;

export interface PanelDefinition {
  name: string,
  pages: {
    [key in PageNames]?: {
      enabled?: PanelFlagPredicate
      inDefaultLayout?: PanelFlagPredicate
    }
  },
  locationPreference?: {
    siblingPanelTypes: PanelType[],
    splitSibling?: boolean
  },
  shouldAdd?: (state: PanelStateModel, page: PageNames, flags: PanelFlags) => boolean
}

export interface PanelNode extends PanelDefinition {
  // Derived from the panel type, so a layout rebuilt for a new mode names the same nodes as the
  // one it replaces. flexlayout generates an id per node otherwise, and a tab that comes back
  // under a new id is a new component to React: the 3D viewer would be torn down and its Forge
  // viewer recreated on every mode switch.
  id: string,
  component: PanelType,
  type: string
}

export interface PanelTypeToggle {
  enabled: boolean
  inDefaultLayout: boolean
}

export type PanelTypeToggles = {
  [panelType in PanelType]?: PanelTypeToggle
};

export type PagePanelModel = { [pageName in PageNames]?: IJsonModel };
export type PagePanelModelByMode = { [pageName in PageNames]?: { mode: InspectionMode, layout: IJsonModel } };
export type PanelNodeByType = { [panelType in PanelType]: PanelDefinition };

const PanelNodesByPanelType: PanelNodeByType = {
  [PanelType.INSPECT_PANEL]: {
    name: "Mode",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true,
        inDefaultLayout: {
          photosOnly: false
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.ELEMENT_GROUPS]
    }
  },

  [PanelType.MINIMAP]: {
    name: "Map",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.ACTION_HISTORY]
    }
  },

  [PanelType.ELEMENT_INSPECTOR]: {
    name: "Inspector",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true,
        inDefaultLayout: {
          photosOnly: false
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.COMMENTS]
    }
  },

  [PanelType.COMMENTS]: {
    name: "Comments",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true,
        inDefaultLayout: {
          photosOnly: false
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.ELEMENT_INSPECTOR]
    }
  },

  [PanelType.VIEWER]: {
    name: "3D Model",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true,
        inDefaultLayout: {
          photosOnly: false
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.PHOTO_VIEWER]
    }
  },

  [PanelType.INSPECT_REPORTS]: {
    name: "Deviation Analysis Report",
    pages: {
      [PageNames.PROJECT_SUMMARY]: {
        enabled: {
          customReportsPanel: true
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.PROJECT_AREAS_MODEL_VIEWER]
    }
  },

  [PanelType.PROGRESS_4D_REPORT_PANEL]: {
    name: "Schedule vs Progress",
    pages: {
      [PageNames.PROJECT_SUMMARY]: {
        enabled: {
          showProgressOnSummary: true,
          progressReportTab: true,
          // showAllReportsOnSummary: true
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.INSPECT_REPORTS, PanelType.CUSTOM_REPORTS_PANEL]
    }
  },

  [PanelType.CUSTOM_REPORTS_PANEL]: { //JDC: Delete if not needed
    name: "Reports",
    pages: {
      [PageNames.PROJECT_SUMMARY]: {
        enabled: (flags) => {
          return flags.showCustomReportsOnSummary || flags.enableNativeReportCharts;
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.PROGRESS_4D_REPORT_PANEL, PanelType.INSPECT_PANEL]
    }
  },

  [PanelType.PROJECT_AREA_SUMMARY]: {
    name: "Area Summary",
    pages: {
      [PageNames.PROJECT_SUMMARY]: {
        enabled: {
          projectAreaSummaryPanel: true
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.PROJECT_FILES]
    }
  },

  [PanelType.PROJECT_AREAS_MODEL_VIEWER]: {
    name: "Summary View",
    pages: {
      [PageNames.PROJECT_SUMMARY]: {
        enabled: {
          projectAreasModelViewerPanel: true,
          desktopOnly: true
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.INSPECT_REPORTS]
    }
  },

  [PanelType.PROJECT_FILES]: {
    name: "Files",
    pages: {
      [PageNames.PROJECT_SUMMARY]: {
        enabled: true
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.PROJECT_AREA_SUMMARY]
    }
  },

  [PanelType.ELEMENT_GROUPS]: {
    name: "Groups",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: {
          groupsPanel: true
        },
        inDefaultLayout: {
          groupsPanel: true,
          photosOnly: false
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.INSPECT_PANEL]
    }
  },

  [PanelType.ACTION_HISTORY]: {
    name: "History",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true,
        inDefaultLayout: {
          photosOnly: false
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.MINIMAP]
    }
  },

  [PanelType.PHOTO_VIEWER]: {
    name: "Photo Viewer",
    pages: {
      [PageNames.PROJECT_VIEWER]: {
        enabled: true,
        inDefaultLayout: {
          photosOnly: true
        }
      }
    },
    locationPreference: {
      siblingPanelTypes: [PanelType.VIEWER],
      splitSibling: true
    }
  }
};

export const ELEMENT_INFO_PANEL_NAME = "Element Info";

// WARNING: panel layouts persist to local storage with each tab's name baked in.
// Renaming a panel in PanelNodesByPanelType leaves everyone who has already
// opened the viewer on the old name, so names must be resolved from the panel
// type here at render time instead.
export function getPanelDisplayName(panelType: PanelType, flags: PanelFlags): string {
  if (panelType === PanelType.ELEMENT_INSPECTOR && flags.elementInfoPanel) {
    return ELEMENT_INFO_PANEL_NAME;
  }

  return PanelNodesByPanelType[panelType]?.name;
}

export function getPanelNodesByPanelType(panelType: PanelType): PanelNode {
  return {
    ...PanelNodesByPanelType[panelType],
    id: `panel-${panelType}`,
    component: panelType,
    type: "tab"
  };
}

// inspectionMode is only passed once the Explorer configures its panels by mode; without it the
// viewer keeps the single layout it has always had.
export function getDefaultPanelLayout(pageName: PageNames, flags: PanelFlags, inspectionMode?: InspectionMode) {
  if (pageName === PageNames.PROJECT_SUMMARY) {
    return getProjectSummaryPanelsLayout(flags);
  } else if (inspectionMode != null) {
    return getPanelsLayoutForMode(inspectionMode, flags);
  } else {
    return getPanelsLayout(flags);
  }
}

export function getPanelTypeTogglesByPageAndFlags(page: PageNames, flags: PanelFlags): PanelTypeToggles {
  const toggles: PanelTypeToggles = {};

  for (let typeKey in PanelNodesByPanelType) {
    const panelType = typeKey as PanelType;
    const definition = PanelNodesByPanelType[panelType];
    const pageDef = definition.pages[page];
    if (pageDef == null || (pageDef.inDefaultLayout == null && pageDef.enabled == null)) {
      continue;
    }

    let toggle = toggles[panelType];
    if (toggle == null) {
      toggle = {
        enabled: false,
        inDefaultLayout: false
      };
      toggles[panelType] = toggle;
    }

    const inDefaultLayoutPredicate = pageDef.inDefaultLayout;
    const enabledPredicate = pageDef.enabled;

    const inDefaultLayout = testPanelFlag(inDefaultLayoutPredicate, flags);
    const enabled = testPanelFlag(enabledPredicate, flags);

    if (inDefaultLayout == null) {
      if (enabled != null) {
        toggle.inDefaultLayout = enabled;
      }
    } else {
      toggle.inDefaultLayout = inDefaultLayout;
    }

    if (enabled == null) {
      if (inDefaultLayout != null) {
        toggle.enabled = inDefaultLayout;
      }
    } else {
      toggle.enabled = enabled;
    }
  }

  return toggles;
}

export function testPanelFlag(enabled: PanelFlagPredicate, flags: PanelFlags): boolean | null {
  if (enabled == null) {
    return null;
  }

  if (isBoolean(enabled)) {
    return enabled;
  } else if (isFunction(enabled)) {
    return enabled(flags)
  }

  return doPanelFlagsMatch(enabled, flags);
}

export function getEnabledPanelTypesByPageAndFlags(page: PageNames, flags: PanelFlags): PanelType[] {
  const toggles = getPanelTypeTogglesByPageAndFlags(page, flags);
  const enabledTypes: PanelType[] = [];

  for (let typeKey in toggles) {
    const toggle = toggles[typeKey];
    if (toggle != null && toggle.enabled) {
      enabledTypes.push(typeKey as PanelType);
    }
  }

  return enabledTypes;
}

export default PanelType;
